import { Button } from '@oysterjs/ui/button';

import { joinWithAnd } from '~/components/email-templates/blank-fields';
import { FieldReferenceText } from '~/components/email-templates/field-reference-text';
import { InlineNotice } from '~/components/inline-notice';

const isOwnLabel = (label: string) => label.startsWith('your ');

/**
 * "the renewal date, your name and carrier": one "the", before the first label that doesn't
 * already say whose it is.
 */
const blankFieldPhrases = (labels: string[]) => {
  const firstPlainIndex = labels.findIndex((label) => !isOwnLabel(label));
  return labels.map((label, index) => (index === firstPlainIndex ? `the ${label}` : label));
};

/**
 * What the notice says about the blank fields, such as "your name and the renewal date". A bulk
 * email can't be redrafted from its composer, so its notice only says to type them in.
 */
export const missingDetailsCopy = (
  blankFieldLabels: string[],
  { offersRedraft = true }: { offersRedraft?: boolean } = {}
) => {
  const isOne = blankFieldLabels.length === 1;
  const pronoun = isOne ? 'it' : 'them';
  return {
    title: `Cara couldn't find ${joinWithAnd(blankFieldPhrases(blankFieldLabels))}.`,
    description: offersRedraft
      ? `Cara left a blank instead of guessing. Type ${pronoun} into the highlighted spots, or tell Cara where to find ${pronoun} and ask it to redraft.`
      : `Type ${pronoun} into the highlighted ${isOne ? 'spot' : 'spots'}.`
  };
};

/** Explains the blank fields above the email, with a way to fill in or drop the first one. */
export const BlankFieldNotice = (props: {
  blankFieldLabels: string[];
  /** The first blank that is in the body, which the actions work on. */
  firstBodyBlankLabel: string | undefined;
  onFillIn: (label: string) => void;
  onRemoveSentence: (label: string) => void;
  offersRedraft?: boolean;
}) => {
  const { title, description } = missingDetailsCopy(props.blankFieldLabels, {
    offersRedraft: props.offersRedraft
  });
  const label = props.firstBodyBlankLabel;
  return (
    <InlineNotice
      className="mb-3"
      tone="warning"
      title={title}
      description={description}
      actions={
        label && (
          <>
            <Button color="white" onClick={() => props.onFillIn(label)}>
              Fill in <FieldReferenceText text={`[[${label}]]`} surface="button" />
            </Button>
            <Button plain onClick={() => props.onRemoveSentence(label)}>
              Remove the sentence
            </Button>
          </>
        )
      }
    />
  );
};
